import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Menu, Flex, Dropdown, Button, Space } from "antd";
import {
  PlusOutlined,
  PlusSquareFilled,
  CodepenOutlined,
} from "@ant-design/icons";
import { SidebarThreadIcon, UnorderedListIcon } from "../../assets/icons";
import { useLocation, useNavigate, useParams } from "react-router";
import ChannelListItem from "./ChannelListItem";
import DirectMessageListItem from "./DirectMessageListItem";
import CreateChannelModal from "./modals/CreateChannelModal";
import BrowseChannelModal from "./modals/BrowseChannelModel";
import DirectMessageModal from "./modals/DirectMessageModal";
import ArchivedUsersModal from "./modals/ArchivedUsersModal";
import FindChannelModal from "./modals/FindChannelModal";
import {
  handleApiError,
  addMuteStatus,
  localStorageUtil,
  updateDataById,
  requestNotificationPermission,
} from "../../utils/CommonFunctions";
import { showBrowserNotification } from "../../utils/notificationHelper";
import ApiService from "../../API/apiService";
import { API_ENDPOINTS } from "../../API/endpoints";
import { useUserSelfStore } from "../../store/apis/useUserStore";
import { useBridgeStore } from "../../store/apis/useBridgeStore";
import { useSocketListener } from "../../socket/hooks/useSocketListener";
import { socketEvents } from "../../settings/socketEventName";
import { Helmet } from "react-helmet";

const loggedInUser = localStorageUtil.getItem("userData");
const restrictedUserIds = ["686bc06a6602c2ee3814a6e2", "6752e47ffa9a7055f07272a9", "6752fa7818388a42f7c3aa00", "6752e499fa9a7055f07272d4"]; // Example restricted user IDs

// Constants
const INITIAL_MODAL_STATE = {
  activeModal: "",
  modalPayload: "",
};

// Sidebar data sources that can be refetched independently.
const ALL_SECTIONS = ["channels", "chats", "favs", "threads", "notifications"];

// Attach each chat's unread thread replies, so a chat whose only unread items are
// thread replies still counts as unread in the sidebar
const withUnreadThreads = (list, threadCounts) =>
  list.map((item) =>
    threadCounts[item._id] ? { ...item, unreadThreadCount: threadCounts[item._id] } : item
  );

// triggerSidebarRefresh(ts, type) types that only need part of the sidebar refetched.
// Any other type (including the default "all") refreshes everything.
const REFRESH_TYPE_SECTIONS = {
  threads: ["threads"],
  chat: ["chats", "favs"],
  channelSeen: ["channels", "favs"],
};

// Events that arrive together (a message fires both the socket listener below and
// the refresh trigger from the open chat) share one round of requests.
const REFRESH_COALESCE_MS = 150;

const INITIAL_OPEN_KEYS = [
  "favorites",
  "channels",
  "direct-messages",
  "unread-messages",
  "notification-alerts",
  "archived-users",
];

const SidebarContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId, channelId } = useParams();
  const userSelfData = useUserSelfStore((state) => state.userSelfData);
  const setJumpConfig = useBridgeStore((state) => state.setJumpConfig);
  const draftRefresh = useBridgeStore((state) => state.draftRefresh);

  const syncMuteConfig = useBridgeStore((state) => state.syncMuteConfig);

  // State management
  const [modalConfig, setModalConfig] = useState(INITIAL_MODAL_STATE);
  const [openKeys, setOpenKeys] = useState(INITIAL_OPEN_KEYS);

  const [channelList, setChannelList] = useState([]);
  const [chatList, setChatList] = useState([]);
  const [archivedChatList, setArchivedChatList] = useState([]);
  const [favUserData, setFavUserData] = useState([]);
  const [favChannelData, setFavChannelData] = useState([]);
  const [notificationChannels, setNotificationChannels] = useState([]);
  const [unreadThreadCount, setUnreadThreadCount] = useState(0);
  const [unreadThreadsByChat, setUnreadThreadsByChat] = useState({ channels: {}, users: {} });
  const [allNotificationCount, setAllNotificationCount] = useState(0);

  // Memoized values
  const hasFavorites = useMemo(
    () => favUserData.length > 0 || favChannelData.length > 0,
    [favUserData.length, favChannelData.length]
  );

  const selectedChannel = useMemo(
    () => channelList.find((channel) => channel._id === channelId),
    [channelList, channelId]
  );

  const selectedChat = useMemo(
    () => chatList.find((chat) => chat._id === userId),
    [chatList, userId]
  );

  // API calls - Optimized with proper dependencies
  const getUnreadThreadCountApi = useCallback(async () => {
    if (!userSelfData?._id) return;
    try {
      const response = await ApiService.get(API_ENDPOINTS.unreadThreadsCount);
      if (response?.data && response?.status === 1) {
        setUnreadThreadCount(response.data.count);
        setUnreadThreadsByChat({
          channels: response.data.channels || {},
          users: response.data.users || {},
        });
      }
    } catch (error) {
      handleApiError(error);
    }
  }, [userSelfData?._id]);

  const getChannelListApi = useCallback(async () => {
    if (!userSelfData?._id) return;
    try {
      const userDataFromStorage = localStorageUtil.getItem("userData");
      const response = await ApiService.get(API_ENDPOINTS.getChannelList);
      if (response.status == 1) {
        let processedData = addMuteStatus(
          response.data,
          userDataFromStorage,
          "mute_channels"
        );
        setChannelList(processedData);

        // Cache default channel ID
        const defaultItem = processedData.find((item) => item.isDefault);
        if (defaultItem?._id) {
          localStorage.setItem("defaultChannelId", defaultItem._id);
        }
      }
    } catch (error) {
      handleApiError(error);
    }
  }, [userSelfData?._id]);

  const getChatListApi = useCallback(async () => {
    if (!userSelfData?._id) return;

    try {
      const userDataFromStorage = localStorageUtil.getItem("userData");
      const response = await ApiService.post(API_ENDPOINTS.getChatList, {
        userId: userSelfData?._id,
      });
      if (response.status == 1) {
        let processedData = addMuteStatus(
          response.data.chatList,
          userDataFromStorage,
          "mute_users"
        );
        setChatList(processedData);
        let processedArchived = addMuteStatus(
          response.data.archivedList || [],
          userDataFromStorage,
          "mute_users"
        );
        setArchivedChatList(processedArchived);
      }
    } catch (error) {
      handleApiError(error);
    }
  }, [userSelfData?._id]);

  const getFavListApi = useCallback(async () => {
    if (!userSelfData?._id) return;

    try {
      const userDataFromStorage = localStorageUtil.getItem("userData");

      const response = await ApiService.post(API_ENDPOINTS.getFavApi, {
        userId: userSelfData?._id,
      });
      if (response?.data && response?.status === 1) {
        let processedData = addMuteStatus(
          response.data.favouriteChannels,
          userDataFromStorage,
          "mute_channels"
        );
        setFavChannelData(processedData);
        let processedDataFavUser = addMuteStatus(
          response.data.chatList,
          userDataFromStorage,
          "mute_users"
        );
        setFavUserData(processedDataFavUser);
      }
    } catch (error) {
      handleApiError(error);
    }
  }, [userSelfData?._id]);

  const getNotificationChannelsApi = useCallback(async () => {
    if (!userSelfData?._id) return;
    try {
      const response = await ApiService.get(API_ENDPOINTS.getNotificationChannels);
      if (response?.status === 1) {
        setNotificationChannels(response.data || []);
      }
    } catch (error) {
      handleApiError(error);
    }
  }, [userSelfData?._id]);

  const loadSections = async (sections = ALL_SECTIONS) => {
    const fetchers = {
      channels: getChannelListApi,
      chats: getChatListApi,
      favs: getFavListApi,
      threads: getUnreadThreadCountApi,
      notifications: getNotificationChannelsApi,
    };
    await Promise.all(sections.map((section) => fetchers[section]()));
  };

  const loadData = () => loadSections(ALL_SECTIONS);

  // Always points at the latest render's fetchers, so a refresh scheduled earlier
  // never runs with stale closures.
  const loadSectionsRef = useRef(loadSections);
  loadSectionsRef.current = loadSections;
  const pendingSectionsRef = useRef(new Set());
  const refreshTimerRef = useRef(null);

  const scheduleRefresh = useCallback((sections = ALL_SECTIONS) => {
    sections.forEach((section) => pendingSectionsRef.current.add(section));
    if (refreshTimerRef.current) return;
    refreshTimerRef.current = setTimeout(() => {
      const pending = [...pendingSectionsRef.current];
      pendingSectionsRef.current.clear();
      refreshTimerRef.current = null;
      loadSectionsRef.current(pending);
    }, REFRESH_COALESCE_MS);
  }, []);

  useEffect(() => () => clearTimeout(refreshTimerRef.current), []);

  // Initial data loading
  useEffect(() => {
    loadData();
  }, [userSelfData?._id]);

  // Refresh requests from the chat views. Subscribed directly (not via a hook value)
  // so every trigger in a burst is seen, not just the last one React renders.
  useEffect(
    () =>
      useBridgeStore.subscribe((state, prevState) => {
        if (state.sidebarRefresh === prevState.sidebarRefresh) return;
        scheduleRefresh(REFRESH_TYPE_SECTIONS[state.refreshType] || ALL_SECTIONS);
      }),
    [scheduleRefresh]
  );

  // Opening a chat marks it seen on the server; clear its badge right away instead of
  // waiting for the round trip. A later refresh restores the real value if that failed.
  useEffect(() => {
    if (!channelId) return;
    setChannelList((prev) => updateDataById(prev, channelId, { unreadCount: 0 }));
    setFavChannelData((prev) =>
      updateDataById(prev, channelId, { unreadCount: 0, unseenMessagesCount: 0 })
    );
  }, [channelId]);

  useEffect(() => {
    if (!userId) return;
    setChatList((prev) => updateDataById(prev, userId, { unseenMessagesCount: 0 }));
    setFavUserData((prev) =>
      updateDataById(prev, userId, { unseenMessagesCount: 0 })
    );
  }, [userId]);

  // Event handlers - Memoized for performance
  const handleThreadClick = useCallback(() => {
    navigate("/elsnertech/unread/threads");
    setJumpConfig({});
  }, [navigate]);

  const handleDraftClick = useCallback(() => {
    navigate("/elsnertech/draft");
    setJumpConfig({});
  }, [navigate]);

  const handleNotificationChannelClick = useCallback((channel) => {
    navigate(`/elsnertech/notifications/${channel._id}`);
    setJumpConfig({});
  }, [navigate]);

  const handleDirectMessageClick = useCallback(
    (item, isThreadUnSeen = 0) => {
      if (item?._id) {
        navigate(`/elsnertech/message/${item._id}`, {
          state: {
            unseenMessagesCount: item.unseenMessagesCount,
            isThreadUnSeen: Boolean(isThreadUnSeen)
          },
        });
      }
      setJumpConfig({});
    },
    [navigate]
  );

  const handleChannelClick = useCallback(
    (channel, isThreadUnSeen = 0) => {
      if (channel?._id) {
        navigate(`/elsnertech/channels/${channel._id}`, {
          state: {
            unseenMessagesCount:
              channel?.unseenMessagesCount || channel?.unreadCount,
            isThreadUnSeen: Boolean(isThreadUnSeen)
          },
        });
      }
      setJumpConfig({});
    },
    [navigate]
  );

  const handleModalUpdate = useCallback((config) => {
    setModalConfig((prev) => ({ ...prev, ...config }));
  }, []);

  const handleDmListUpdate = useCallback((id, updates, shouldDelete) => {
    setChatList((prev) => updateDataById(prev, id, updates, shouldDelete));
  }, []);

  const handleArchivedDmListUpdate = useCallback((id, updates, shouldDelete) => {
    setArchivedChatList((prev) => updateDataById(prev, id, updates, shouldDelete));
  }, []);

  const handleChannelListUpdate = useCallback((id, updates, shouldDelete) => {
    setChannelList((prev) => updateDataById(prev, id, updates, shouldDelete));
  }, []);

  const handleFavDmListUpdate = useCallback((id, updates, shouldDelete) => {
    setFavUserData((prev) => updateDataById(prev, id, updates, shouldDelete));
  }, []);

  const handleFavChannelListUpdate = useCallback(
    (id, updates, shouldDelete) => {
      setFavChannelData((prev) =>
        updateDataById(prev, id, updates, shouldDelete)
      );
    },
    []
  );

  const onOpenChange = useCallback((keys) => {
    // Keep notification-alerts always open — cannot be collapsed
    const next = keys.includes("notification-alerts") ? keys : [...keys, "notification-alerts"];
    setOpenKeys(next);
  }, []);

  const socketCallbacks = useMemo(
    () => ({
      handleUserUpdated: () => {
        loadData();
      },
      handleMemberRemoval: (data) => {
        if (data.data[0]?.receivers === userSelfData?._id) {
          if (data?.routeId === channelId) {
            loadData();
            const channel = localStorage.getItem("defaultChannelId");
            localStorage.setItem("current_id", channel);
            navigate(`/elsnertech/channels/${channel}`);
          } else {
            loadData();
          }
          localStorageUtil.removeItem(`draft-${data?.routeId}`);
          const count = getTotalValidDraftCount();
          setDraftCount(count);
        }
      },
    }),
    [userSelfData?._id, channelId]
  );

  useSocketListener(
    socketEvents.USER_UPDATED,
    socketCallbacks.handleUserUpdated,
    [userSelfData?._id, channelId, userId]
  );

  useSocketListener(
    socketEvents.REMOVE_MEMBER_N,
    socketCallbacks.handleMemberRemoval,
    [userSelfData?._id, channelId, userId]
  );

  // Refresh sidebar counts when any new message notification arrives.
  // This handles cases where no Channel/Message component is mounted
  // (e.g. Threads page, Draft page) so the unread badge updates in real-time.
  useSocketListener(
    socketEvents.NOTIFICATIONS,
    useCallback(
      ({ type, routeId, message_type, payload } = {}) => {
        if (type === "reactionChannel" || type === "deletedMessageChannel") return;

        // A thread reply never changes a chat badge (those count top-level messages
        // only), just the Threads badge — and that applies even when the reply landed
        // in the chat that is currently open.
        if (payload?.isReply) {
          scheduleRefresh(["threads"]);
          return;
        }

        const isNotifChannel = notificationChannels.some((c) => c._id === routeId);
        if (!isNotifChannel && (routeId === channelId || routeId === userId)) return;

        // Bump the badge immediately; the refresh below replaces it with the real value.
        if (message_type === "channel") {
          const bump = (item) =>
            item._id === routeId
              ? { ...item, unreadCount: (item.unreadCount || 0) + 1 }
              : item;
          setChannelList((prev) => prev.map(bump));
          setFavChannelData((prev) =>
            prev.map((item) =>
              item._id === routeId
                ? {
                    ...item,
                    unseenMessagesCount: (item.unseenMessagesCount || 0) + 1,
                  }
                : item
            )
          );
        } else if (message_type === "message") {
          const bump = (item) =>
            item._id === routeId
              ? { ...item, unseenMessagesCount: (item.unseenMessagesCount || 0) + 1 }
              : item;
          setChatList((prev) => prev.map(bump));
          setFavUserData((prev) => prev.map(bump));
        }

        // Only refetch the lists this kind of message can change.
        if (message_type === "notification" || isNotifChannel) {
          scheduleRefresh(["notifications"]);
        } else if (message_type === "channel") {
          scheduleRefresh(["channels", "favs"]);
        } else if (message_type === "message") {
          scheduleRefresh(["chats", "favs"]);
        } else {
          scheduleRefresh(ALL_SECTIONS);
        }
      },
      [channelId, userId, notificationChannels, scheduleRefresh]
    )
  );

  // Global browser notification + sound for incoming messages.
  // This lives in the always-mounted Sidebar so notifications fire on EVERY
  // route (Threads, Draft, Notifications, etc.), not only while a DM/Channel
  // view happens to be mounted. The Message/Channel views no longer trigger
  // browser notifications themselves, so this is the single source of truth
  // and cannot double-fire. The Notification & Alerts view (NotificationChannelView)
  // still renders richer, channel-specific notifications for the channel it has
  // open, so we skip exactly that one case here to avoid notifying twice.
  useSocketListener(
    socketEvents.NOTIFICATIONS,
    useCallback(
      ({
        tagged_users = [],
        channel_name,
        message,
        type,
        data,
        message_type,
        routeId,
      } = {}) => {
        // Reactions / deletions never produced a browser notification.
        if (type === "reactionChannel" || type === "deletedMessageChannel") return;

        // Skip the notification channel currently open in NotificationChannelView,
        // which already shows its own (richer) notification for it.
        if (location.pathname === `/elsnertech/notifications/${routeId}`) return;

        requestNotificationPermission();
        showBrowserNotification(
          channel_name
            ? `Elsner Connect: ${channel_name}`
            : "Elsner Connect: Direct Message",
          message,
          type,
          userSelfData,
          data?.[0]?.sender,
          message_type,
          routeId,
          tagged_users
        );
      },
      [location.pathname, userSelfData]
    )
  );

  // Memoized dropdown menu items
  const channelDropdownItems = useMemo(
    () => [
      {
        key: "create-channel",
        label: "Create new channel",
        icon: <PlusOutlined style={{ fontSize: "14px", color: "#A1A1A1" }} />,
        onClick: () => handleModalUpdate({ activeModal: "CreateChannelModal" }),
      },
      {
        key: "browse-channels",
        label: "Browse channels",
        icon: <UnorderedListIcon />,
        onClick: () => handleModalUpdate({ activeModal: "BrowseChannelModal" }),
      },
    ],
    [handleModalUpdate]
  );

  const [draftCount, setDraftCount] = useState(0);

  const getTotalValidDraftCount = () => {
    const count = Object.keys(localStorage)
      .filter((key) => key.startsWith("draft-"))
      .map((key) => {
        const value = localStorage.getItem(key);
        try {
          const parsed = JSON.parse(value);
          if (
            typeof parsed === "object" &&
            parsed !== null &&
            parsed.inputValue &&
            parsed.inputValue.trim() !== ""
          ) {
            return parsed;
          }
        } catch {
          // Ignore invalid JSON
        }
        return null;
      })
      .filter(Boolean).length;

    return count;
  };

  useEffect(() => {
    const count = getTotalValidDraftCount();
    setDraftCount(count);
  }, [draftRefresh]);

  // Optimized menu items builder
  const buildSidebarMenuItems = useMemo(() => {
    const menuItems = [];
    const channels = withUnreadThreads(channelList, unreadThreadsByChat.channels);
    const chats = withUnreadThreads(chatList, unreadThreadsByChat.users);
    const favChannels = withUnreadThreads(favChannelData, unreadThreadsByChat.channels);
    const favUsers = withUnreadThreads(favUserData, unreadThreadsByChat.users);

    // Threads section
    menuItems.push({
      key: "threads",
      label: (
        <Flex align="center" gap="10px" className="thread">
          <span className="Thread-icon">
            <SidebarThreadIcon />
          </span>
          <div className="msg-wrapper">
            <span className="name">Threads</span>
            {unreadThreadCount > 0 && (
              <span className="unread-msg-count">{unreadThreadCount}</span>
            )}
          </div>
        </Flex>
      ),
      onClick: handleThreadClick,
    });

    // Draft section
    menuItems.push({
      key: "draft",
      label: (
        <Flex align="center" gap="10px" className="thread">
          <span className="Thread-icon">
            <CodepenOutlined />
          </span>
          <div className="msg-wrapper">
            <span className="name">Draft</span>
            {draftCount > 0 && (
              <span className="unread-msg-count">{draftCount}</span>
            )}
          </div>
        </Flex>
      ),
      onClick: handleDraftClick,
    });


    // {
    //   restrictedUserIds?.includes(loggedInUser?._id) ?
      menuItems.push({
        key: "notification-alerts",
        label: (
          <Flex align="center" gap="small">
            <span className="icon-arrow">
              <i className="fa-solid fa-angle-right"></i>
            </span>
            <span>Notification &amp; Alerts</span>
          </Flex>
        ),
        children: notificationChannels.map((channel) => {
          const name = channel.name || channel.notificationType || '?';
          const notifIcon = <i className="fa-solid fa-bell" />;
          return {
            key: `notification-channel-${channel._id}`,
            label: (
              <div className="msg-wrapper">
                <span className="name">{name}</span>
                {channel.unreadCount > 0 && (
                  <span className="unread-msg-count">{channel.unreadCount}</span>
                )}
              </div>
            ),
            icon: notifIcon,
            className: location.pathname === `/elsnertech/notifications/${channel._id}`
              ? "ant_menu_item_selected"
              : "",
            onClick: () => handleNotificationChannelClick(channel),
          };
        }),
      })
    //   : menuItems.push({})
    // }
    // Unread Messages section (conditional)
    const unreadItems = [];

    // Unread channels from channelList
    const unreadChannels = channels.filter(
      (channel) => channel.unreadCount > 0 || channel.unreadThreadCount > 0
    );
    if (unreadChannels.length > 0) {
      unreadItems.push(
        ...unreadChannels.map((channel) => ({
          key: `unread-channel-${channel._id}`,
          label: (
            <ChannelListItem
              key={channel._id}
              channel={channel}
              getFavListApi={getFavListApi}
              getChannelListApi={getChannelListApi}
              isFavoriteList={false}
              userSelfData={userSelfData}
              onListUpdate={handleChannelListUpdate}
            />
          ),
          className: channelId === channel._id ? "ant_menu_item_selected" : "",
          icon: channel.isPrivate ? (
            <i className="fa-solid fa-lock" style={{ color: "#FFFFFFB8" }} />
          ) : (
            <i className="fa-solid fa-globe" />
          ),
          onClick: () => handleChannelClick(channel, unreadThreadCount),
        }))
      );
    }

    // Unread direct messages from chatList
    const unreadChats = chats.filter(
      (chat) => chat.unseenMessagesCount > 0 || chat.unreadThreadCount > 0
    );
    if (unreadChats.length > 0) {
      unreadItems.push(
        ...unreadChats.map((user, index) => ({
          key: `unread-dm-${user._id}`,
          label: (
            <DirectMessageListItem
              item={user}
              getFavListApi={getFavListApi}
              getChatListApi={getChatListApi}
              userSelfData={userSelfData}
              onListUpdate={handleDmListUpdate}
            />
          ),
          className: userId === user._id ? "ant_menu_item_selected" : "",
          onClick: () => handleDirectMessageClick(user, unreadThreadCount),
        }))
      );
    }

    // Add Unread Messages section if there are unread items
    menuItems.push({
      key: "unread-messages",
      label: (
        <Flex align="center" gap="small">
          <span className="icon-arrow">
            <i className="fa-solid fa-angle-right"></i>
          </span>
          <span>Unread Messages</span>
          {unreadItems.length > 0 && <span className="unread-msg-count">{unreadItems.length}</span>}
        </Flex>
      ),
      children: unreadItems,
    });

    // Favorites section (conditional)
    if (hasFavorites) {
      const favChildren = [];

      // Favorite users
      if (favUsers.length > 0) {
        favChildren.push(
          ...favUsers.map((item) => ({
            key: `favDm-${item._id}`,
            label: (
              <DirectMessageListItem
                item={{ ...item, isFavorite: true }}
                getFavListApi={getFavListApi}
                getChatListApi={getChatListApi}
                isFavoriteList={true}
                userSelfData={userSelfData}
                onListUpdate={handleFavDmListUpdate}
              />
            ),
            className: userId === item._id ? "ant_menu_item_selected" : "",
            onClick: () => handleDirectMessageClick(item),
          }))
        );
      }

      // Favorite channels
      if (favChannels.length > 0) {
        favChildren.push(
          ...favChannels.map((channel) => ({
            key: `favChannel-${channel._id}`,
            label: (
              <ChannelListItem
                key={channel._id}
                channel={{
                  ...channel,
                  isFavorite: true,
                  unreadCount:
                    channel.unseenMessagesCount || channel.unreadCount,
                }}
                getFavListApi={getFavListApi}
                getChannelListApi={getChannelListApi}
                isFavoriteList={true}
                userSelfData={userSelfData}
                onListUpdate={handleFavChannelListUpdate}
              />
            ),
            className:
              channelId === channel._id ? "ant_menu_item_selected" : "",
            icon: channel.isPrivate ? (
              <i className="fa-solid fa-lock" style={{ color: "#FFFFFFB8" }} />
            ) : (
              <i className="fa-solid fa-globe" />
            ),
            onClick: () => handleChannelClick(channel),
          }))
        );
      }

      menuItems.push({
        key: "favorites",
        label: (
          <Flex align="center" gap="small">
            <span className="icon-arrow">
              <i className="fa-solid fa-angle-right"></i>
            </span>
            <span>Favorites</span>
          </Flex>
        ),
        children: favChildren,
      });
    }

    // Channels section
    const channelChildren = [];
    const readChannels = channels.filter(
      (data) => data.unreadCount == 0 && !data.unreadThreadCount
    );

    if (readChannels.length > 0) {
      channelChildren.push(
        ...readChannels.map((channel) => ({
          key: `channel-${channel._id}`,
          label: (
            <ChannelListItem
              key={channel._id}
              channel={channel}
              getFavListApi={getFavListApi}
              getChannelListApi={getChannelListApi}
              isFavoriteList={false}
              userSelfData={userSelfData}
              onListUpdate={handleChannelListUpdate}
            />
          ),
          className: channelId === channel._id ? "ant_menu_item_selected" : "",
          icon: channel.isPrivate ? (
            <i className="fa-solid fa-lock" style={{ color: "#FFFFFFB8" }} />
          ) : (
            <i className="fa-solid fa-globe" />
          ),
          onClick: () => handleChannelClick(channel),
        }))
      );
    }

    channelChildren.push({
      key: "add-channel",
      label: (
        <Flex justify="space-between" align="center" gap="middle">
          <Dropdown
            menu={{ items: channelDropdownItems }}
            className="ec-dropdown sidebar-channel-dropdown"
            dropdownRender={(menu) => (
              <div className="ec-dropdown__menu sidebar-channel-dropdown__menu">
                {menu}
              </div>
            )}
            trigger={["click"]}
          >
            <span>
              <PlusSquareFilled /> Add Channel
            </span>
          </Dropdown>
        </Flex>
      ),
      className: "add-channel-btn",
    });

    menuItems.push({
      key: "channels",
      label: (
        <Flex align="center" gap="small">
          <span className="icon-arrow">
            <i className="fa-solid fa-angle-right"></i>
          </span>
          <span>Channels</span>
        </Flex>
      ),
      children: channelChildren,
    });

    // Selected channel when channels section is closed
    if (selectedChannel && !openKeys.includes("channels")) {
      menuItems.push({
        key: `selected-channel-${selectedChannel._id}`,
        label: (
          <ChannelListItem
            key={selectedChannel._id}
            channel={selectedChannel}
            getFavListApi={getFavListApi}
            getChannelListApi={getChannelListApi}
            hideMoreOptions={true}
            userSelfData={userSelfData}
          />
        ),
        className: "ant_menu_item_selected static-item-selected",
        icon: selectedChannel.isPrivate ? (
          <i className="fa-solid fa-lock" style={{ color: "#FFFFFFB8" }} />
        ) : (
          <i className="fa-solid fa-globe" />
        ),
        onClick: () => handleChannelClick(selectedChannel),
      });
    }

    // Direct Messages section
    const dmChildren = chats.filter(data => data.unseenMessagesCount == 0 && !data.unreadThreadCount).map((user, index) => ({
      key: `direct-message-${index}`,
      label: (
        <DirectMessageListItem
          item={user}
          getFavListApi={getFavListApi}
          getChatListApi={getChatListApi}
          userSelfData={userSelfData}
          onListUpdate={handleDmListUpdate}
        />
      ),
      className: userId === user._id ? "ant_menu_item_selected" : "",
      onClick: () => handleDirectMessageClick(user),
    }));

    menuItems.push({
      key: "direct-messages",
      label: (
        <Flex justify="space-between" align="center" gap="small">
          <Flex
            align="center"
            gap="small"
            onClick={(e) => {
              e.stopPropagation();
              const newKeys = openKeys.includes("direct-messages")
                ? openKeys.filter((key) => key !== "direct-messages")
                : [...openKeys, "direct-messages"];
              onOpenChange(newKeys);
            }}
            style={{ cursor: "pointer", flex: 1 }}
          >
            <span className="icon-arrow">
              <i className="fa-solid fa-angle-right"></i>
            </span>
            <span>Direct Messages</span>
          </Flex>
          <Button
            type="text"
            className="add-direct-msg-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleModalUpdate({ activeModal: "DirectMessageModal" });
            }}
          >
            <PlusOutlined />
          </Button>
        </Flex>
      ),
      children: dmChildren,
    });

    // Selected chat when direct messages section is closed
    if (selectedChat && !openKeys.includes("direct-messages")) {
      menuItems.push({
        key: `selected-chat-${selectedChat._id}`,
        label: (
          <DirectMessageListItem
            item={selectedChat}
            getFavListApi={getFavListApi}
            getChatListApi={getChatListApi}
            hideMoreOptions={true}
            userSelfData={userSelfData}
          />
        ),
        className: "ant_menu_item_selected static-item-selected",
        onClick: () => handleDirectMessageClick(selectedChat),
      });
    }

    // Archived Users section
    if (archivedChatList.length > 0) {
      const archivedDmChildren = archivedChatList.map((user, index) => ({
        key: `archived-dm-${index}`,
        label: (
          <DirectMessageListItem
            item={user}
            getFavListApi={getFavListApi}
            getChatListApi={getChatListApi}
            userSelfData={userSelfData}
            onListUpdate={handleArchivedDmListUpdate}
          />
        ),
        className: userId === user._id ? "ant_menu_item_selected" : "",
        onClick: () => handleDirectMessageClick(user),
      }));

      menuItems.push({
        key: "archived-users",
        label: (
          <Flex justify="space-between" align="center" gap="small">
            <Flex
              align="center"
              gap="small"
              onClick={(e) => {
                e.stopPropagation();
                const newKeys = openKeys.includes("archived-users")
                  ? openKeys.filter((key) => key !== "archived-users")
                  : [...openKeys, "archived-users"];
                onOpenChange(newKeys);
              }}
              style={{ cursor: "pointer", flex: 1 }}
            >
              <span className="icon-arrow">
                <i className="fa-solid fa-angle-right"></i>
              </span>
              <span>Archived Users</span>
            </Flex>
            <Button
              type="text"
              className="add-direct-msg-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleModalUpdate({ activeModal: "ArchivedUsersModal" });
              }}
            >
              <PlusOutlined />
            </Button>
          </Flex>
        ),
        children: archivedDmChildren,
      });
    }

    return menuItems;
  }, [
    draftCount,
    unreadThreadCount,
    unreadThreadsByChat,
    hasFavorites,
    favUserData,
    favChannelData,
    channelList,
    chatList,
    selectedChannel,
    selectedChat,
    openKeys,
    userId,
    channelId,
    channelDropdownItems,
    getFavListApi,
    getChannelListApi,
    getChatListApi,
    handleDirectMessageClick,
    handleChannelClick,
    handleNotificationChannelClick,
    notificationChannels,
    handleModalUpdate,
    onOpenChange,
    location.pathname,
    archivedChatList,
    handleArchivedDmListUpdate,
  ]);

  // Render modal based on active modal
  const renderModal = useMemo(() => {
    const { activeModal } = modalConfig;

    if (!activeModal) return null;

    const modalProps = {
      userSelfData,
      onModalUpdate: handleModalUpdate,
    };

    switch (activeModal) {
      case "FindChannelModal":
        return (
          <FindChannelModal
            {...modalProps}
            channelList={channelList}
            chatList={chatList}
          />
        );
      case "CreateChannelModal":
        return (
          <CreateChannelModal
            {...modalProps}
            getChannelListApi={getChannelListApi}
          />
        );
      case "BrowseChannelModal":
        return (
          <BrowseChannelModal
            {...modalProps}
            getChannelListApi={getChannelListApi}
          />
        );
      case "DirectMessageModal":
        return (
          <DirectMessageModal {...modalProps} getChatListApi={getChatListApi} />
        );
      case "ArchivedUsersModal":
        return (
          <ArchivedUsersModal {...modalProps} getChatListApi={getChatListApi} />
        );
      default:
        return null;
    }
  }, [
    modalConfig.activeModal,
    userSelfData,
    handleModalUpdate,
    channelList,
    chatList,
    getChannelListApi,
    getChatListApi,
  ]);

  const totalCount = useMemo(() => {
    const sumUnread = (users, property) =>
      users.reduce((total, user) => total + (user[property] || 0), 0);

    return (
      sumUnread(channelList, "unreadCount") +
      sumUnread(chatList, "unseenMessagesCount") +
      sumUnread(favUserData, "unseenMessagesCount") +
      sumUnread(favChannelData, "unseenMessagesCount")
    );
  }, [channelList, chatList, favUserData, favChannelData]);

  useEffect(() => {
    setAllNotificationCount(totalCount);
  }, [totalCount]);

  useEffect(() => {
    document.title =
      allNotificationCount > 0
        ? `(${allNotificationCount})* New Messages`
        : "Welcome to Elsner e-Connect Portal";

    const favicon = document.querySelector("link[rel='icon']");
    if (favicon) {
      favicon.href =
        allNotificationCount > 0 ? "/faviconN.png" : "/favicon-Defualt.png";
    } else {
      const link = document.createElement("link");
      link.rel = "icon";
      link.href =
        allNotificationCount > 0 ? "/faviconN.png" : "/favicon-Defualt.png";
      document.head.appendChild(link);
    }

    const observer = new MutationObserver(() => {
      if (favicon)
        favicon.href =
          allNotificationCount > 0 ? "/faviconN.png" : "/favicon-Defualt.png";
    });

    observer.observe(document.head, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [allNotificationCount]);

  // ✅ Update PWA Notification Badge
  useEffect(() => {
    if ("setAppBadge" in navigator && "clearAppBadge" in navigator) {
      if (allNotificationCount > 0) {
        navigator
          .setAppBadge(allNotificationCount)
          .catch((err) => console.error("Badge API Error:", err));
      } else {
        navigator
          .clearAppBadge()
          .catch((err) => console.error("Badge API Error:", err));
      }
    }
  }, [allNotificationCount]);

  useEffect(() => {
    if (syncMuteConfig.identifiers) {
      handleDmListUpdate(syncMuteConfig.identifiers, {
        isMutedByMe: syncMuteConfig.status,
      });
    }
  }, [syncMuteConfig]);

  return (
    <>
      <Helmet>
        <title>
          {allNotificationCount > 0
            ? `(${allNotificationCount})* New Messages`
            : "Welcome to Elsner e-Connect Portal"}
        </title>
      </Helmet>

      <Flex
        justify="space-between"
        align="center"
        gap="small"
        className="sidebar-header"
      >
        <Dropdown
          menu={{ items: [] }}
          className="ec-dropdown et-dropdown"
          placement="top"
        >
          <Space style={{ cursor: "default" }}>Elsner technologies</Space>
        </Dropdown>
      </Flex>

      <Flex align="center" gap="small" className="sidebar-filters-wrapper">
        <Button
          className="search-channel-btn"
          onClick={() => handleModalUpdate({ activeModal: "FindChannelModal" })}
        >
          <i className="fa-solid fa-magnifying-glass search-icon"></i>
          <span>Start Chat</span>
        </Button>
      </Flex>

      <div className="sidebar-categories">
        <div className="scroll-view">
          <Menu
            onOpenChange={onOpenChange}
            className="sidebar-menu"
            mode="inline"
            openKeys={openKeys}
            items={buildSidebarMenuItems}
            style={{ marginTop: "0px" }}
          />
        </div>
      </div>

      {renderModal}
    </>
  );
};

export default SidebarContainer;
